<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">electric_car</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Trips</h2>
          <p class="text-sm text-gray-500">Browse and manage trips.</p>
        </div>
      </div>
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-200">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
          </div>
          <export-button
            v-if="!$store.getters.corporateRole"
            :downloadUrl="reportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Status -->
          <div class="w-full sm:w-36">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Status</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="selectedFilter"
              @change="getRides"
            >
              <option :selected="selectedFilter === 'all'" value="all">
                All
              </option>
              <option
                :selected="selectedFilter === 'COMPLETED'"
                value="Completed"
              >
                Completed
              </option>
              <option
                :selected="selectedFilter === 'CANCELLED'"
                value="Cancelled"
              >
                Cancelled
              </option>
              <option
                :selected="selectedFilter === 'STARTED'"
                value="Started"
              >
                Started
              </option>
            </select>
          </div>

          <!-- Type -->
          <div class="w-full sm:w-40">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Type</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="deliveryTrips"
              @change="getRides"
            >
              <option :selected="deliveryTrips === 'all'" value="all">
                Non-Delivery
              </option>
              <option
                :selected="deliveryTrips === 'delivery'"
                value="delivery"
              >
                Delivery
              </option>
            </select>
          </div>

          <template v-if="!$store.getters.corporateRole">
            <!-- Driver -->
            <div class="relative w-full sm:w-56">
              <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Driver</label>
              <div class="relative mt-1.5">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span class="text-base text-gray-500 material-icons-round">search</span>
                </span>
                <input
                  v-model="selectedDriver"
                  @keyup="searchDriver"
                  class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="Search drivers..."
                />
              </div>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-if="driverSearchResults.length"
                  class="absolute inset-x-0 z-50 mt-2"
                >
                  <div
                    class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                  >
                    <ul>
                      <li
                        v-for="(result, index) in driverSearchResults"
                        :key="result._id"
                        @click="selectDriver(index)"
                        class="flex items-center px-3 py-2 space-x-3 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                      >
                        <img
                          class="object-cover w-10 h-10 rounded-full"
                          :src="result.profileImage"
                          :alt="`${result.firstName} ${result.lastName}`"
                        />
                        <div>
                          <p class="text-sm font-semibold text-gray-800">
                            {{ `${result.firstName} ${result.lastName}` }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ result.phoneNumber }}
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Passenger -->
            <div class="relative w-full sm:w-56">
              <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Passenger</label>
              <div class="relative mt-1.5">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span class="text-base text-gray-500 material-icons-round">search</span>
                </span>
                <input
                  v-model="selectedPassenger"
                  @keyup="searchPassenger"
                  class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="Search passenger..."
                />
              </div>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-if="passengerSearchResults.length"
                  class="absolute inset-x-0 z-50 mt-2"
                >
                  <div
                    class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                  >
                    <ul>
                      <li
                        v-for="(result, index) in passengerSearchResults"
                        :key="result._id"
                        @click="selectPassenger(index)"
                        class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                      >
                        <p class="text-sm text-gray-800">
                          {{ `${result.firstName} ${result.lastName}` }}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </transition>
            </div>

            <!-- User -->
            <div class="relative w-full sm:w-56">
              <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">User</label>
              <div class="relative mt-1.5">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span class="text-base text-gray-500 material-icons-round">search</span>
                </span>
                <input
                  v-model="selectedUser"
                  @keyup="searchUser"
                  class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="Search user..."
                />
              </div>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-if="userSearchResults.length"
                  class="absolute inset-x-0 z-50 mt-2"
                >
                  <div
                    class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                  >
                    <ul>
                      <li
                        v-for="(result, index) in userSearchResults"
                        :key="result._id"
                        @click="selectUser(index)"
                        class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                      >
                        <p class="text-sm text-gray-800">
                          {{ `${result.firstName} ${result.lastName}` }}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </transition>
            </div>
          </template>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-600 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-200">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in [
                    'Dispatcher',
                    'Scheduled',
                    'Passenger Name',
                    'Driver Name',
                    'Pickup / End Time',
                    'Pick / Drop Address',
                    'Type',
                    'Vehicle Type',
                    'Status',
                  ]"
                  :key="col"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
                <th class="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody class="relative" v-if="rides.length">
              <tr
                v-for="(trip, index) in rides"
                :key="trip._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Dispatcher -->
                <td class="px-5 py-4 text-gray-500 whitespace-no-wrap">
                  {{ personLabel(trip.dispatcher, "—") }}
                </td>
                <!-- Scheduled -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex px-2 py-1 text-xs font-medium rounded-md"
                    :class="trip.schedule ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 bg-gray-100'"
                  >
                    {{ trip.schedule ? "Scheduled" : "Now" }}
                  </span>
                </td>
                <!-- Passenger -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(trip.passenger))"
                    >{{ initials(personLabel(trip.passenger)) }}</span>
                    <span class="font-medium text-gray-900">{{ personLabel(trip.passenger, "Unknown") }}</span>
                  </div>
                </td>
                <!-- Driver -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(trip.driver))"
                    >{{ initials(personLabel(trip.driver)) }}</span>
                    <span class="text-gray-700">{{ personLabel(trip.driver, "Unknown") }}</span>
                  </div>
                </td>
                <!-- Pickup / End time -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-600 material-icons-round">schedule</i>
                    <span>{{ trip.pickupTimestamp ? formatDate(trip.pickupTimestamp) : "—" }}</span>
                  </div>
                  <p class="mt-1 ml-6 text-xs text-gray-600">
                    {{
                      trip.endTimestamp
                        ? formatDate(trip.endTimestamp)
                        : trip.status === "CANCELLED"
                        ? "Cancelled trip"
                        : "—"
                    }}
                  </p>
                </td>
                <!-- Route -->
                <td class="px-5 py-4">
                  <div class="flex">
                    <div class="flex flex-col items-center mr-3">
                      <i class="text-base leading-none text-green-500 material-icons-round">my_location</i>
                      <span class="flex-1 w-px my-1 border-l border-gray-300 border-dashed"></span>
                      <i class="text-base leading-none text-red-500 material-icons-round">flag</i>
                    </div>
                    <div class="flex flex-col justify-between min-w-0">
                      <p class="text-gray-700 truncate max-w-xs">
                        {{ trip.pickUpAddress ? trip.pickUpAddress.name : "—" }}
                      </p>
                      <p class="text-gray-700 truncate max-w-xs">
                        {{ trip.dropOffAddress ? trip.dropOffAddress.name : "—" }}
                      </p>
                    </div>
                  </div>
                </td>
                <!-- Type -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span class="inline-flex px-2 py-1 text-xs font-medium text-gray-600 capitalize rounded-md bg-gray-100">
                    {{ trip.type }}
                  </span>
                </td>
                <!-- Vehicle type -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ trip.vehicleType ? trip.vehicleType.name : "—" }}
                </td>
                <!-- Status -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none capitalize rounded-full"
                    :class="statusClass(trip.status)"
                  >
                    <span class="w-2 h-2 mr-2 rounded-full" :class="statusDotClass(trip.status)"></span>
                    {{ prettyStatus(trip.status) }}
                  </span>
                </td>
                <!-- Action -->
                <td class="px-5 py-4 text-right whitespace-no-wrap">
                  <a
                    :href="'/rides/' + trip._id + '/show'"
                    target="self"
                    title="View trip"
                    class="inline-flex items-center justify-center w-10 h-10 text-gray-600 rounded-lg transition-colors duration-150 hover:bg-taxiye-100 hover:text-taxiye-600"
                  >
                    <i class="material-icons-round">visibility</i>
                  </a>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="10" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-600">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No trips found</span>
                    <span class="mt-1 text-xs text-gray-600">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
// import moment from "moment";
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      rides: [],
      openDropdown: false,
      selectedAccount: "",
      loading: true,
      accounts: [],
      accountId: null,
      selectedFilter: "all",
      
      deliveryTrips: "all",

      selectedDriver: "",
      selectedPassenger: "",
      selectedUser: "",

      driverSearchResults: [],
      userSearchResults: [],
      passengerSearchResults: [],

      driverId: null,

      userId: null,

      passengerId: null,

      range: {
        start: null,
        end: null,
      },
    };
  },
  components: {
    ExportButton,
    Pagination,
  },
  watch: {
    currentPage() {
      this.getRides();
    },
    perPage() {
      this.currentPage === 1 ? this.getRides() : (this.currentPage = 1);
    },
    range: {
      handler() {
        this.getRides();
      },
      deep: true,
    },
    $route() {
      if (this.$route.query.status) {
        this.selectedFilter = this.$route.query.status;
      }
    },
  },
  created() {
    // var sd = new Date();
    // sd.setDate(0);
    // sd.setDate(1);
    // var ed = new Date();
    // ed.setMonth(ed.getMonth() + 1);
    // ed.setDate(0);
    // this.range.start = sd;
    // this.range.end = ed;

    if (this.$route.query.status) {
      this.selectedFilter = this.$route.query.status;
    }
    if (this.$route.query.page) {
      this.currentPage = this.$route.query.page;
    }
    if (this.$route.query.passenger) {
      this.passengerId = this.$route.query.passenger;
      this.$misc
        .get("users/" + this.$route.query.passenger)
        .then(({ data: { firstName, lastName, phoneNumber } }) => {
          this.selectedPassenger = `${firstName} ${lastName} - ${phoneNumber}`;
        });
    }
    if (this.$route.query.driver) {
      this.driverId = this.$route.query.driver;
      this.$misc
        .get("drivers/" + this.$route.query.driver)
        .then(({ data }) => {
          const { firstName, lastName, phoneNumber } = data.driver
          this.selectedDriver = `${firstName} ${lastName} - ${phoneNumber}`;
          this.selectedDriverObj = data.driver
        });
    }

    this.getRides();
    this.$misc
      .get("/accounts")
      .then((response) => (this.accounts = response.data.data))
      .catch((err) => console.log(err));
  },
  computed: {
    driverFilter() {
      return this.$route.query.driver || null;
    },
    riderFilter() {
      return this.$route.query.passenger || null;
    },
    reportDownloadUrl() {
      return (
        "trips/export?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              driver: this.driverId ? this.driverId : undefined,
              dispatcher: this.userId ? this.userId : undefined,
              passenger: this.passengerId ? this.passengerId : undefined,
              status:
                this.selectedFilter && this.selectedFilter != "all"
                  ? this.selectedFilter
                  : undefined,
              ...(this.range || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  methods: {
    searchDriver(e) {
      if (e.target.value.trim() === "") {
  this.selectedDriverObj = null;
        this.selectedDriver = '';
        this.driverId = null;
        this.driverSearchResults = [];
        const query = { ...this.$route.query, driver: null };
        this.$router.push({ query });
        this.getRides();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.driverSearchResults = [];
          this.$misc
            .get("drivers/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.driverSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.driverSearchResults = [];
        }
      }
    },

    searchUser(e) {
      if (e.target.value.trim() === "") {
        this.userSearchResults = [];
        this.userId = null;
        this.getRides();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.userSearchResults = [];
          this.$misc
            .get("accounts/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.userSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.userSearchResults = [];
        }
      }
    },

    searchPassenger(e) {
      if (e.target.value.trim() === "") {
         this.selectedPassengerObj = null;
        this.selectedPassenger = '';
        this.passengerSearchResults = [];
        this.passengerId = null;
        const query = { ...this.$route.query, passenger: null };
        this.$router.push({ query });

        this.getRides();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.passengerSearchResults = [];
          this.$misc
            .get("users/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.passengerSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.passengerSearchResults = [];
        }
      }
    },

    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriverObj = driver;
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.driverId = driver._id;
      this.driverSearchResults = [];
      const query = { ...this.$route.query, driver: this.driverId };
      this.$router.push({ query });
      this.getRides();
    },

    selectUser(index) {
      let user = this.userSearchResults[index];
      this.selectedUserObj = user;
      this.selectedUser = `${user.firstName} ${user.lastName}`;
      this.userId = user._id;
      this.userSearchResults = [];
      this.getRides();
    },

    selectPassenger(index) {
      let user = this.passengerSearchResults[index];
      this.selectedPassengerObj = user;
      this.selectedPassenger = `${user.firstName} ${user.lastName} - ${user.phoneNumber}`;
      this.passengerId = user._id;
      this.passengerSearchResults = [];
      const query = { ...this.$route.query, passenger: this.passengerId };
      this.$router.push({ query });

      this.getRides();
    },

    formatDate(date) {
      // return moment(date).format("MMM D, YYYY HH:mm");
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    statusClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("cancel") || s.includes("no_drivers")) return "text-red-700 bg-red-100";
      if (s.includes("complet")) return "text-green-700 bg-green-100";
      if (s.includes("start") || s.includes("running") || s.includes("progress")) return "text-orange-700 bg-orange-100";
      return "text-gray-700 bg-gray-100";
    },
    statusDotClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("cancel") || s.includes("no_drivers")) return "bg-red-500";
      if (s.includes("complet")) return "bg-green-500";
      if (s.includes("start") || s.includes("running") || s.includes("progress")) return "bg-orange-500";
      return "bg-gray-400";
    },
    prettyStatus(status) {
      return (status || "—").replace(/_/g, " ").toLowerCase();
    },
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    getRides() {
      this.loading = true;
      var path = `trips?limit=${this.perPage}&page=${this.currentPage}&populate=["driver","passenger","vehicleType","dispatcher"]`;

      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }

      if (this.passengerId) {
        path += "&passenger=" + this.passengerId;
      }

      if (this.userId) {
        path += "&dispatcher=" + this.userId;
      }

      if (this.selectedFilter && this.selectedFilter != "all") {
        path += "&status=" + this.selectedFilter;
      }

      if (this.deliveryTrips && this.deliveryTrips != "all") {
        path += "&deliveryTrips='true'"
      }

      if (this.range && this.range.start && this.range.end) {
        path += "&from=" + this.range.start + "&to=" + this.range.end;
      }

      this.$misc
        .get(path)
        .then((response) => {
          this.rides = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>
